import React from 'react';
import { createRoot } from 'react-dom/client';
import './i18n';
import App from './App';
import {
  ENABLE_MOCKS,
  IS_TEST,
  ENVIRONMENT,
  SENTRY_DSN,
  SENTRY_SAMPLE_RATE,
  SENTRY_ENABLE_LOCALHOST,
} from './config';
import * as Sentry from '@sentry/react';
import { browserTracingIntegration } from '@sentry/browser';

const isLocalhost = Boolean(window.location.hostname === 'localhost');

if (
  !IS_TEST &&
  !ENABLE_MOCKS &&
  (!isLocalhost || SENTRY_ENABLE_LOCALHOST) &&
  SENTRY_DSN
) {
  Sentry.init({
    dsn: SENTRY_DSN,
    tracesSampleRate: SENTRY_SAMPLE_RATE,
    environment: ENVIRONMENT,
    integrations: [browserTracingIntegration()],
    ignoreErrors: [
      'Non-Error exception captured',
      'Non-Error promise rejection captured',
      /Load failed/,
      /NetworkError when attempting to fetch resource/,
    ],
    denyUrls: [
      /\.netlify\/scripts\/rum/,
      /ingesteer\.services-prod\.nsvcs\.net/,
      /extensions\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      // Third-party Google reCAPTCHA script: inside the iOS WKWebView it throws an uncatchable SyntaxError from its internal postMessage calls.
      /www\.gstatic\.com\/recaptcha/,
    ],
    beforeSend(event) {
      const message = event.exception?.values?.[0]?.value ?? '';
      // Drop browser-extension errors and iOS DOM mutation errors.
      // ignoreErrors is bypassed when errors are explicitly captured via
      // captureException() (e.g. by Sentry.ErrorBoundary), so beforeSend is used to catch these cases as well.
      if (
        /Invalid call to runtime\.sendMessage\(\)/i.test(message) ||
        /The object can not be found here/i.test(message) ||
        /Failed to execute 'removeChild' on 'Node'/i.test(message)
      ) {
        return null;
      }
      return event;
    },
  });
}

if (ENABLE_MOCKS && !IS_TEST) {
  (async () => {
    const { initializeMocks } = await import('./utils/mocks');
    await initializeMocks();
  })();
}

// When Vite fails to load a dynamically imported chunk (e.g. after a new
// deployment that changes content-hashed filenames), reload the page so the
// browser picks up the latest index.html and the correct chunk URLs instead
// of showing an unrecoverable error to the user.
window.addEventListener('vite:preloadError', () => {
  window.location.reload();
});

const container = document.getElementById('root');
if (container) {
  const root = createRoot(container);
  root.render(<App />);
} else {
  console.error('Root container not found');
}
